/**
 * How the planet turns under a finger: it is heavy. It follows the finger with some lag and at a
 * fraction of its speed, keeps turning after the finger lets go, and never spins faster than a cap,
 * so a swipe stays a walk and never flies you round the planet in a second.
 */
export const SPIN = Object.freeze({
  /** The planet moves this share of the finger's distance. */
  gain: 0.72,
  /** How fast it catches up with the finger, per second (higher is lighter). */
  follow: 12,
  /** The most it turns per frame at 60 fps, in finger pixels. */
  maxSpeed: 16,
  /** What it keeps of its speed every frame after the finger lets go. */
  friction: 0.955,
  /** A finger that hasn't moved for this long is holding still. */
  stillMs: 90,
  /** Below this it has stopped. */
  rest: 0.05,
} as const);

export interface Spin {
  x: number;
  y: number;
}

/** The finger's speed in pixels per frame, and when it was last measured. */
export interface Finger {
  x: number;
  y: number;
  at: number;
}

/** One frame: the planet's velocity (pixels per frame) chases the finger, or glides to a stop. Mutates `vel`. */
export function stepSpin(
  vel: Spin,
  finger: Finger,
  dragging: boolean,
  dt: number,
  now: number,
): void {
  if (dragging) {
    const still = now - finger.at > SPIN.stillMs;
    const tx = still ? 0 : finger.x * SPIN.gain;
    const ty = still ? 0 : finger.y * SPIN.gain;
    const k = 1 - Math.exp(-dt * SPIN.follow);
    vel.x += (tx - vel.x) * k;
    vel.y += (ty - vel.y) * k;
  } else {
    const decay = Math.pow(SPIN.friction, dt * 60);
    vel.x *= decay;
    vel.y *= decay;
  }
  const speed = Math.hypot(vel.x, vel.y);
  if (speed > SPIN.maxSpeed) {
    vel.x *= SPIN.maxSpeed / speed;
    vel.y *= SPIN.maxSpeed / speed;
  } else if (speed < SPIN.rest) {
    vel.x = 0;
    vel.y = 0;
  }
}
